import { type EmergencyDeclarationDto } from "@crowdcoursing/api/openapi";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { emergencyLabelForKey } from "~ui/templates/AdminTemplate/BubbleGroups/emergencyQuickResponseActions";

function formatStartedAt(iso?: string | null): string {
  if (!iso) {
    return "—";
  }
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) {
    return iso;
  }
  return dt.toLocaleString(DateTime.DATETIME_SHORT);
}

function declaredByLine(d: EmergencyDeclarationDto): string {
  const f = d.declaredByUserFirstName?.trim();
  const l = d.declaredByUserLastName?.trim();
  if (f && l) {
    return `${f} ${l}`;
  }
  if (f) {
    return f;
  }
  if (l) {
    return l;
  }
  return "—";
}

export interface ActiveEmergencyDeclarationsSectionProps {
  declarations: EmergencyDeclarationDto[];
}

/**
 * Explains active emergency dispatches for staff or law enforcement: human-readable type, drill vs live, timing.
 */
export function ActiveEmergencyDeclarationsSection({ declarations }: ActiveEmergencyDeclarationsSectionProps) {
  if (declarations.length === 0) {
    return null;
  }

  const heading = declarations.length === 1 ? "Active emergency" : "Active emergencies";

  return (
    <Paper
      aria-labelledby="active-emergency-declarations-heading"
      component="section"
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        boxSizing: "border-box",
        maxWidth: "100%",
        mb: 2,
        minWidth: 0,
        p: { sm: 2, xs: 1.5 },
      }}
      variant="outlined"
    >
      <Typography component="h2" id="active-emergency-declarations-heading" sx={{ mb: 1 }} variant="subtitle1">
        {heading}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        The school has reported the following dispatch{declarations.length > 1 ? "es" : ""}. Each block states whether
        it is a drill or a live emergency.
      </Typography>
      <Stack spacing={2}>
        {declarations.map((d) => {
          const key = d.emergencyKey ?? "";
          const typeLabel = emergencyLabelForKey(key);
          const drill = Boolean(d.isDrill);
          const rowKey = d.id ?? `${key}-${d.startedAtUtc ?? ""}`;

          return (
            <Box key={rowKey}>
              {drill ? (
                <Alert severity="info" variant="outlined">
                  <Typography component="div" sx={{ fontWeight: 700, mb: 1 }} variant="subtitle2">
                    Drill — not a real emergency
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
                    This dispatch is a planned exercise. It does not indicate a real-world incident by itself.
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Emergency type:</strong> {typeLabel}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Started:</strong> {formatStartedAt(d.startedAtUtc)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Declared by:</strong> {declaredByLine(d)}
                    </Typography>
                  </Stack>
                </Alert>
              ) : (
                <Alert severity="warning" variant="filled">
                  <Typography component="div" sx={{ fontWeight: 700, mb: 1 }} variant="subtitle2">
                    Live emergency
                  </Typography>
                  <Typography sx={{ mb: 1.5, opacity: 0.95 }} variant="body2">
                    This dispatch is not marked as a drill. Treat it as a live incident unless your agency protocols say
                    otherwise.
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Emergency type:</strong> {typeLabel}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Started:</strong> {formatStartedAt(d.startedAtUtc)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Declared by:</strong> {declaredByLine(d)}
                    </Typography>
                  </Stack>
                </Alert>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
